import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      localStorage.removeItem('userId');
      window.location.href = '/influencer/login?expired=true';
    }
    return Promise.reject(error);
  }
);

// Create a new automation
export const createAutomation = async (automationData) => {
  try {
    const response = await api.post('/automation/api/automations', automationData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to create automation' };
  }
};

// Get all automations for the current user
export const getUserAutomations = async () => {
  try {
    const response = await api.get('/automation/api/automations');
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch automations' };
  }
};

// Get a single automation by ID
export const getAutomationById = async (id) => {
  try {
    const response = await api.get(`/automation/api/automations/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to fetch automation' };
  }
};

// Update an automation
export const updateAutomation = async (id, automationData) => {
  try {
    const response = await api.put(`/automation/api/automations/${id}`, automationData);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to update automation' };
  }
};

// Delete an automation
export const deleteAutomation = async (id) => {
  try {
    const response = await api.delete(`/automation/api/automations/${id}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to delete automation' };
  }
};

// Toggle automation status (active/inactive)
export const toggleAutomationStatus = async (id) => {
  try {
    const response = await api.patch(`/automation/api/automations/${id}/toggle`);
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to toggle automation status' };
  }
};

/**
 * On-demand AI analysis of an automation post's comments.
 *
 * Cost note: the server only calls Gemini on a cache miss (or when
 * comments changed). This is also gated server-side to PAID plans only
 * (trial users get a 403). Safe to call on an explicit user click, and
 * must NEVER be wired to auto-run on mount.
 *
 * On error, rethrows an object that includes the backend `code`
 * (e.g. NOT_ENOUGH_COMMENTS, PAID_SUBSCRIPTION_REQUIRED) so callers can
 * branch their UX.
 */
export const analyzeAutomationComments = async (id) => {
  try {
    const response = await api.post(
      `/automation/api/automations/${id}/comments/analyze`
    );
    return response.data;
  } catch (error) {
    throw error.response?.data || { error: 'Failed to analyze comments' };
  }
};

